import React, { useEffect, useMemo, useState } from "react";
import { API_URLS } from "../../../api/apiUrls";
import { getApi } from "../../../api/api_instance";
import CardsBorder from "../../../components/Cards/CardsBorder";

const CustomerSetup = ({ clientId, value, setValue }) => {
  const [setupData, setSetupData] = useState(null); // store setup
  const [clientData, setClientData] = useState(null); // store client info
  const [loading, setLoading] = useState(false);

  const licenseLabels = {
    sub_admin: "Sub Admin",
    local_supervisor: "Local Supervisor",
    user: "User",
    employee: "Employees",
    vendor: "Vendors",
    management_view: "Management View",
    group_view: "Group View",
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getApi({
        apiUrl: API_URLS.GET_CUSTOMER_SETUP_DATA(clientId),
      });

      if (response.success) {
        const client = response.data?.client;
        const setup = response.data?.setup;

        setClientData(client);
        setSetupData(setup);

        // Update completion value to parent component
        setValue(client?.isCompleted);

        console.log("Customer setup:", client?.isCompleted);
      }
    } catch (error) {
      console.error("client not found", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) fetchData();
  }, [clientId]);

  const hasLicenses = useMemo(() => {
    if (!setupData?.license) return false;
    return Object.values(setupData.license).some((lic) => (lic?.total || 0) > 0);
  }, [setupData]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <span className="text-[#06513D] font-semibold text-lg animate-pulse">Loading Customer Setup Data...</span>
      </div>
    );
  }

  if (!clientData && !setupData) {
    return (
      <div className="text-center py-10 text-gray-500 italic">
        No setup information available for this customer.
      </div>
    );
  }

  return (
    <div className="mt-4 max-w-auto m-[20px] flex flex-col gap-6 bg-[#fff]">
      {/* 1. Client & Organization Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {clientData && (
          <CardsBorder>
            <div className="p-5">
              <h4 className="text-lg font-bold text-[#06513D] border-b pb-2 mb-4">
                Client Information
              </h4>
              <div className="flex flex-col gap-3 text-sm text-gray-700">
                {/* <div className="flex justify-between">
                  <span className="font-semibold text-gray-600">Client Name:</span>
                  <span>{clientData.name || "N/A"}</span>
                </div> */}
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-600">Client Email:</span>
                  <span>{clientData.email || "N/A"}</span>
                </div>

                <div className="flex justify-between">
                  <span className="font-semibold text-gray-600">Contact Number:</span>
                  <span>{setupData?.contact_no || "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-600">Registered On:</span>
                  <span>{clientData.createdAt ? new Date(clientData.createdAt).toLocaleDateString("en-IN", { day: 'numeric', month: 'long', year: 'numeric' }) : "N/A"}</span>
                </div>
              </div>
            </div>
          </CardsBorder>
        )}

        {setupData && (
          <CardsBorder>
            <div className="p-5">
              <h4 className="text-lg font-bold text-[#06513D] border-b pb-2 mb-4">
                Organization Information
              </h4>
              <div className="flex flex-col gap-3 text-sm text-gray-700">
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-600">Organization Name:</span>
                  <span>{setupData.organizationName || "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-600">Headquarter Location:</span>
                  <span>{setupData.headquarter_location || "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-600">Total Operating Locations:</span>
                  <span>{setupData.no_of_location || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-600">Organization Size:</span>
                  <span>{setupData.organizationSize ? `${setupData.organizationSize} Employees` : "N/A"}</span>
                </div>
              </div>
            </div>
          </CardsBorder>
        )}
      </div>

      {/* 2. Sustainability Maturity & Industry Details */}
      {setupData && (
        <CardsBorder>
          <div className="p-5">
            <h4 className="text-lg font-bold text-[#06513D] border-b pb-2 mb-4">
              Sustainability & Industry Details
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm text-gray-700">
              <div className="flex justify-between border-b py-1">
                <span className="font-semibold text-gray-600">Industry / Sector:</span>
                <span>{setupData.industry?.join(", ") || "N/A"}</span>
              </div>
               <div className="flex justify-between border-b py-1">
                <span className="font-semibold text-gray-600">Other Industry Specified:</span>
                <span>{setupData.otherIndustry?.join(", ") || "None"}</span>
              </div>
              <div className="flex justify-between border-b py-1">
                <span className="font-semibold text-gray-600">Reporting Year Type:</span>
                <span className="capitalize">{setupData.yearType || "N/A"} Year</span>
              </div>
              <div className="flex justify-between border-b py-1">
                <span className="font-semibold text-gray-600">Assessment period:</span>
                <span>{setupData.assessment_period || "N/A"}</span>
              </div>
             
              <div className="flex justify-between border-b py-1">
                <span className="font-semibold text-gray-600">Has Net Zero Targets:</span>
                <span>{setupData.net_zero_target?.hasTarget ? "Yes" : "No"}</span>
              </div>
              <div className="flex justify-between border-b py-1">
                <span className="font-semibold text-gray-600">Net Zero Year / Details:</span>
                <span>{setupData.net_zero_target?.details?.year || "N/A"}</span>
              </div>
              <div className="flex justify-between border-b py-1 col-span-1 md:col-span-2">
                <span className="font-semibold text-gray-600">Tracking Carbon Emissions:</span>
                <span>{setupData.carbon_emission_track?.hasTarget ? `Yes (Method: ${setupData.carbon_emission_track?.details?.method || "N/A"})` : "No"}</span>
              </div>
            </div>
          </div>
        </CardsBorder>
      )}

      {/* 3. License Breakdown */}
      {setupData && setupData.license && (
        <CardsBorder>
          <div className="p-5">
            <h4 className="text-lg font-bold text-[#06513D] border-b pb-2 mb-4">
              License Requirements
            </h4>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-100 rounded-lg">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="py-3 px-4 text-left text-sm font-semibold text-[#06513D]">License Type</th>
                    <th className="py-3 px-4 text-center text-sm font-semibold text-[#06513D]">Total Selected</th>
                    <th className="py-3 px-4 text-center text-sm font-semibold text-[#06513D]">Price per License</th>
                    <th className="py-3 px-4 text-right text-sm font-semibold text-[#06513D]">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {hasLicenses ? (
                    Object.entries(setupData.license).map(([key, value]) => {
                      const label = licenseLabels[key] || key;
                      const count = value?.total || 0;
                      const price = value?.price || 0;
                      const total = count * price;
                      if (count === 0) return null;
                      return (
                        <tr key={key} className="border-b text-sm text-gray-700 hover:bg-gray-50">
                          <td className="py-3 px-4 text-left font-medium">{label}  <div className="text-[10px] text-gray-400">
                                per user/month
                              </div></td> 
                         
                          <td className="py-3 px-4 text-center font-semibold">{count}</td>
                          <td className="py-3 px-4 text-center">₹{price}</td>
                          <td className="py-3 px-4 text-right font-semibold text-[#06513D]">₹{total}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" className="py-4 text-center text-gray-400 italic">No licenses selected</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {hasLicenses && (
              <div className="flex justify-end mt-3 pr-4">
                <span className="text-sm font-bold text-gray-700 mr-2">Licenses Subtotal:</span>
                <span className="text-sm font-bold text-[#06513D]">₹{setupData.licensesSubtotal || 0}</span>
              </div>
            )}
          </div>
        </CardsBorder>
      )}

      {/* 4. Services Opted & Pricing Details */}
      {setupData && (
        <CardsBorder>
          <div className="p-5">
            <h4 className="text-lg font-bold text-[#06513D] border-b pb-2 mb-4">
              Sustainability Services Required
            </h4>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-100 rounded-lg">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="py-3 px-4 text-left text-sm font-semibold text-[#06513D]">Opted Service</th>
                    <th className="py-3 px-4 text-right text-sm font-semibold text-[#06513D]">Service Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {setupData.services_required && setupData.services_required.length > 0 ? (
                    setupData.services_required.map((service, index) => (
                      <tr key={index} className="border-b text-sm text-gray-700 hover:bg-gray-50">
                        <td className="py-3 px-4 text-left font-medium">{service.title}</td>
                        <td className="py-3 px-4 text-right font-semibold text-[#06513D]">₹{service.price}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="2" className="py-4 text-center text-gray-400 italic">No services selected</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between items-center mt-4 text-sm text-gray-700 border-t pt-3">
              <div>
                <span className="font-semibold text-gray-600">Number of Locations opted for services:</span>
                <span className="ml-2 font-bold">{setupData.location_opt || 0}</span>
              </div>
              <div>
                <span className="font-bold text-gray-700 mr-2">Services Subtotal:</span>
                <span className="font-bold text-[#06513D]">₹{setupData.servicesSubtotal || 0}</span>
              </div>
            </div>
          </div>
        </CardsBorder>
      )}

      {/* 5. Summary & Total Billing */}
      {setupData && (
        <CardsBorder>
          <div className="p-5">
            <h4 className="text-lg font-bold text-[#06513D] border-b pb-2 mb-4">
              Billing & Cost Summary
            </h4>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-100 rounded-lg">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="py-3 px-4 text-left text-sm font-semibold text-[#06513D]">Billing Item</th>
                    <th className="py-3 px-4 text-right text-sm font-semibold text-[#06513D]">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b text-sm text-gray-700 hover:bg-gray-50">
                    <td className="py-3 px-4 text-left font-medium">Licenses Subtotal</td>
                    <td className="py-3 px-4 text-right font-semibold text-[#06513D]">₹{setupData.licensesSubtotal || 0}</td>
                  </tr>
                  <tr className="border-b text-sm text-gray-700 hover:bg-gray-50">
                    <td className="py-3 px-4 text-left font-medium">Services Subtotal</td>
                    <td className="py-3 px-4 text-right font-semibold text-[#06513D]">₹{setupData.servicesSubtotal || 0}</td>
                  </tr>
                  <tr className="border-b text-sm text-gray-700 hover:bg-gray-50">
                    <td className="py-3 px-4 text-left font-medium">Overall Subtotal</td>
                    <td className="py-3 px-4 text-right font-semibold text-[#06513D]">₹{setupData.overallSubtotal || 0}</td>
                  </tr>
                  <tr className="border-b text-sm text-gray-700 hover:bg-gray-50">
                    <td className="py-3 px-4 text-left font-medium">GST (18%)</td>
                    <td className="py-3 px-4 text-right font-semibold text-[#06513D]">₹{setupData.gstAmount || 0}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="flex justify-between items-center mt-4 text-sm text-gray-700 border-t pt-3">
              <div></div>
              <div>
                <span className="font-bold text-[#06513D] text-base mr-2">Total Amount:</span>
                <span className="font-bold text-[#06513D] text-lg">₹{setupData.totalAmount || 0}</span>
              </div>
            </div>
          </div>
        </CardsBorder>
      )}
      
    </div>
  );
};

export default CustomerSetup;
